import type { CompletedLap, DriverState, DriverTiming, RaceEvent, RaceSnapshot, SpeedTrap } from './race';
import { factKey } from './fact-ledger';

export interface RaceRecord { driverNumber: number; value: number; lap: number | null; atMs: number | null }
export interface RaceRecords {
  fastestLaps: RaceRecord[];
  lapRelay: RaceRecord[];
  speeds: Partial<Record<SpeedTrap, RaceRecord[]>>;
  speedRelay: Partial<Record<SpeedTrap, RaceRecord[]>>;
  sectors: [RaceRecord[], RaceRecord[], RaceRecord[]];
  gains: (RaceRecord & { start: number; position: number })[];
}
const positive = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0;
const knownLap = (value: unknown): value is number => positive(value) && Number.isInteger(value);
const knownTime = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0;
const same = (a: number, b: number) => Math.abs(a - b) < .00001;
const extrema = <T extends RaceRecord>(values: T[], maximum = false): T[] => {
  if (!values.length) return [];
  const best = (maximum ? Math.max : Math.min)(...values.map(value => value.value));
  return values.filter(value => same(value.value, best)).sort((a, b) => a.driverNumber - b.driverNumber);
};
const identity = (record: RaceRecord) => `${record.driverNumber}:${record.lap}:${record.value}`;
interface Observation { driverNumber: number; observedAt: number; record: RaceRecord | null }

/** Untimed readings constrain later records without becoming timestamped relay nodes. */
function succession(observations: Observation[], maximum = false): RaceRecord[] {
  const baselines = new Map<number, number>(), recorded = new Map<number, number>();
  const seen = new Set<string>(), result: RaceRecord[] = [];
  const better = (a: number, b: number) => maximum ? a > b && !same(a, b) : a < b && !same(a, b);
  const bound = () => {
    const values = [...baselines.values(), ...recorded.values()];
    return values.length ? (maximum ? Math.max : Math.min)(...values) : maximum ? -Infinity : Infinity;
  };
  const timeline = observations.map(item => ({ ...item, atMs: item.record?.atMs ?? item.observedAt }))
    .sort((a, b) => a.atMs - b.atMs || Number(a.record?.atMs !== null && a.record !== null) - Number(b.record?.atMs !== null && b.record !== null));
  for (let index = 0; index < timeline.length;) {
    const atMs = timeline[index].atMs;
    const batch: typeof timeline = [];
    while (index < timeline.length && timeline[index].atMs === atMs) batch.push(timeline[index++]);
    for (const item of batch) if (!item.record || item.record.atMs === null) {
      if (item.record) baselines.set(item.driverNumber, item.record.value); else baselines.delete(item.driverNumber);
    }
    const limit = bound();
    const candidates: RaceRecord[] = [];
    for (const { record } of batch) {
      if (!record || record.atMs === null || seen.has(identity(record))) continue;
      seen.add(identity(record));
      const previous = recorded.get(record.driverNumber);
      if (previous === undefined || better(record.value, previous)) recorded.set(record.driverNumber, record.value);
      if (better(record.value, limit)) candidates.push(record);
    }
    // Equal source timestamps have no observable order; retain ties at the batch extremum.
    const winners = extrema(candidates, maximum).sort((a, b) => Number(a.lap === null) - Number(b.lap === null));
    const drivers = new Set<number>();
    for (const record of winners) if (!drivers.has(record.driverNumber)) { drivers.add(record.driverNumber); result.push(record); }
  }
  return result;
}

/** Current records and observed succession; corrections are resolved before computing extrema. */
export function raceRecords(snapshot: RaceSnapshot, events: RaceEvent[] = []): RaceRecords {
  const visible = new Map<string, RaceEvent>();
  for (const event of events) if (knownTime(event.atMs) && event.atMs <= snapshot.atMs) visible.set(factKey(event), event);
  const ordered = [...visible.values()].sort((a, b) => a.atMs - b.atMs);
  const laps = new Map<number, Map<number, CompletedLap>>();
  const remember = (number: number, values: CompletedLap[] | undefined, observedAt: number, fromSnapshot = false) => {
    const map = laps.get(number) ?? new Map<number, CompletedLap>();
    for (const lap of values ?? []) {
      if (!knownLap(lap.lap) || !knownTime(lap.atMs) || lap.atMs > observedAt) continue;
      const prior = map.get(lap.lap);
      // Ordered event corrections beat rolling summaries; explicit deletion/reinstatement still applies.
      if (fromSnapshot && prior && !(lap.deleted === true && !prior.deleted) && !(lap.deleted === false && prior.deleted)) continue;
      map.set(lap.lap, lap);
    }
    laps.set(number, map);
  };
  for (const event of ordered) if (event.type === 'timing') remember(event.driverNumber, event.patch.recentLaps, event.atMs);
  for (const driver of snapshot.drivers) remember(driver.number, driver.recentLaps, snapshot.atMs, true);
  const valid = (lap: CompletedLap) => !lap.deleted && !lap.partial && positive(lap.duration);
  // Only direct laps establish that all three sector values belong to this lap.
  const direct = (lap: CompletedLap) => valid(lap) && lap.provenance === 'direct';
  const canonical = (number: number) => [...(laps.get(number)?.values() ?? [])];
  const normalize = (number: number, value: unknown, lapNumber: unknown, atMs: unknown, observedAt: number): RaceRecord | null => {
    if (!positive(value) || knownTime(atMs) && atMs > observedAt) return null;
    const lap = knownLap(lapNumber) ? lapNumber : null;
    const reference = lap === null ? undefined : laps.get(number)?.get(lap);
    if (reference?.deleted || reference && positive(reference.duration) && !same(reference.duration, value)) return null;
    return { driverNumber: number, value, lap, atMs: knownTime(atMs) ? atMs : null };
  };
  const bestReading = (number: number, timing: Partial<DriverTiming>, observedAt: number) =>
    normalize(number, timing.bestLap, timing.bestLapNumber, timing.bestLapAtMs, observedAt);
  const sectors: RaceRecord[][] = [[], [], []];
  const lapObservations: Observation[] = [];
  for (const driver of snapshot.drivers) for (const lap of canonical(driver.number).filter(direct)) {
    lapObservations.push({ driverNumber: driver.number, observedAt: lap.atMs, record: { driverNumber: driver.number, value: lap.duration!, lap: lap.lap, atMs: lap.atMs } });
    lap.sectors.forEach((value, index) => { if (positive(value)) sectors[index].push({ driverNumber: driver.number, value, lap: lap.lap, atMs: lap.atMs }); });
  }
  for (const event of ordered) {
    if (event.type !== 'timing' || !Object.hasOwn(event.patch, 'bestLap')) continue;
    const record = bestReading(event.driverNumber, event.patch, event.atMs);
    if (!record && event.patch.bestLap !== null) continue;
    lapObservations.push({ driverNumber: event.driverNumber, observedAt: event.atMs, record });
  }
  const best = snapshot.drivers.flatMap(driver => {
    let reported = bestReading(driver.number, driver, snapshot.atMs);
    const completed = canonical(driver.number).filter(direct).map(lap => ({ driverNumber: driver.number, value: lap.duration!, lap: lap.lap, atMs: lap.atMs }));
    if (reported) {
      const matching = completed.filter(lap => same(lap.value, reported!.value) && (reported!.lap === null || lap.lap === reported!.lap));
      if (matching.length === 1) reported = { ...reported, lap: reported.lap ?? matching[0].lap, atMs: matching[0].atMs };
      const known = lapObservations.some(item => item.driverNumber === driver.number && item.record && identity(item.record) === identity(reported!));
      if (!known) {
        // No chronological evidence locates an untimed final reading: conservatively constrain the whole unknown interval.
        lapObservations.push({ driverNumber: driver.number, observedAt: reported.atMs === null ? 0 : snapshot.atMs, record: reported });
      }
    }
    const candidates = [...(reported ? [reported] : []), ...completed];
    return extrema(candidates).slice(0, 1);
  });
  const speeds: RaceRecords['speeds'] = {}, speedRelay: RaceRecords['speedRelay'] = {};
  for (const trap of ['ST', 'I1', 'I2', 'FL'] as const) {
    const changes: Observation[] = [];
    for (const event of ordered) {
      if (event.type !== 'timing' || !event.patch.speedTraps || !Object.hasOwn(event.patch.speedTraps, trap)) continue;
      const sample = event.patch.speedTraps[trap]?.best;
      const record = sample && positive(sample.value) && !(knownTime(sample.atMs) && sample.atMs > event.atMs)
        ? { driverNumber: event.driverNumber, value: sample.value, lap: knownLap(sample.lap) ? sample.lap : null, atMs: knownTime(sample.atMs) ? sample.atMs : null } : null;
      if (!record && sample !== null) continue;
      changes.push({ driverNumber: event.driverNumber, observedAt: event.atMs, record });
    }
    const values = snapshot.drivers.flatMap(driver => {
      const sample = driver.speedTraps?.[trap]?.best;
      if (!sample || !positive(sample.value) || knownTime(sample.atMs) && sample.atMs > snapshot.atMs) return [];
      const record = { driverNumber: driver.number, value: sample.value, lap: knownLap(sample.lap) ? sample.lap : null, atMs: knownTime(sample.atMs) ? sample.atMs : null };
      if (!changes.some(item => item.record && identity(item.record) === identity(record))) changes.push({ driverNumber: driver.number, observedAt: record.atMs === null ? 0 : snapshot.atMs, record });
      return [record];
    });
    speeds[trap] = extrema(values, true);
    speedRelay[trap] = succession(changes, true);
  }
  const gains = snapshot.drivers.flatMap(driver => {
    if (!knownLap(driver.startingPosition) || !knownLap(driver.position) || driver.status === 'RETIRED' || driver.startingPosition <= driver.position) return [];
    return [{ driverNumber: driver.number, value: driver.startingPosition - driver.position, start: driver.startingPosition, position: driver.position, lap: null, atMs: snapshot.atMs }];
  });
  return { fastestLaps: extrema(best), lapRelay: succession(lapObservations), speeds, speedRelay,
    sectors: sectors.map(values => extrema(values)) as RaceRecords['sectors'], gains: extrema(gains, true) };
}

export function recordDriver(record: RaceRecord | undefined, drivers: DriverState[]) {
  return record ? drivers.find(driver => driver.number === record.driverNumber) : undefined;
}
